"use client";

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Slider } from "@/components/ui/slider";
import { Loader2, Move } from "lucide-react";
import { cn } from "@/lib/utils";

export type PdfPageInfo = { pageNumber: number; width: number; height: number };

export type QrPlacementResult = {
  placement: "custom";
  pageNumber: number;
  x: number;
  y: number;
  qrWidth: number;
  addNewPage: boolean;
};

type QrPdfPlacerProps = {
  fileId: string;
  fetchPdfBlob: () => Promise<ArrayBuffer>;
  qrDataUrl: string;
  transactionNumber: string;
  pages: PdfPageInfo[];
  onApply: (result: QrPlacementResult) => void;
  onCancel: () => void;
  applying?: boolean;
};

type PdfJsLib = {
  getDocument: (src: { data: ArrayBuffer }) => { promise: Promise<PdfDoc> };
  GlobalWorkerOptions: { workerSrc: string };
};

type PdfDoc = {
  getPage: (n: number) => Promise<PdfPage>;
};

type PdfPage = {
  getViewport: (opts: { scale: number }) => { width: number; height: number };
  render: (ctx: {
    canvasContext: CanvasRenderingContext2D;
    viewport: { width: number; height: number };
    canvas: HTMLCanvasElement;
  }) => { promise: Promise<void> };
};

let pdfJsLoadPromise: Promise<PdfJsLib> | null = null;

async function loadPdfJs(): Promise<PdfJsLib> {
  if (typeof window === "undefined") {
    throw new Error("PDF preview is only available in the browser");
  }
  if (!pdfJsLoadPromise) {
    pdfJsLoadPromise = import("pdfjs-dist/build/pdf.mjs").then((mod) => {
      const pdfjs = mod as unknown as PdfJsLib;
      pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";
      return pdfjs;
    });
  }
  return pdfJsLoadPromise;
}

export function QrPdfPlacer({
  fileId,
  fetchPdfBlob,
  qrDataUrl,
  transactionNumber,
  pages,
  onApply,
  onCancel,
  applying = false,
}: QrPdfPlacerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);

  const [stageWidth, setStageWidth] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [targetMode, setTargetMode] = useState<"existing" | "new">("existing");
  const [pageNumber, setPageNumber] = useState(1);
  const [canvasPx, setCanvasPx] = useState({ width: 0, height: 0 });
  const [pagePts, setPagePts] = useState({ width: 595, height: 842 });
  const [qrSizePx, setQrSizePx] = useState(96);
  const [qrPos, setQrPos] = useState({ x: 40, y: 40 });
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef({ startX: 0, startY: 0, origX: 0, origY: 0 });
  const pdfDataRef = useRef<ArrayBuffer | null>(null);

  const pageCount = Math.max(pages.length, 1);
  const activePage = pages.find((p) => p.pageNumber === pageNumber) || pages[0];

  const clampQr = useCallback(
    (x: number, y: number, size: number, cw: number, ch: number) => ({
      x: Math.max(0, Math.min(x, Math.max(0, cw - size))),
      y: Math.max(0, Math.min(y, Math.max(0, ch - size))),
    }),
    []
  );

  // Measure stage width once the dialog layout is ready.
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;

    const measure = () => {
      const w = el.clientWidth - 16;
      if (w > 100) setStageWidth(w);
    };

    measure();
    const timer = window.setTimeout(measure, 150);

    const ro = new ResizeObserver(measure);
    ro.observe(el);

    return () => {
      window.clearTimeout(timer);
      ro.disconnect();
    };
  }, []);

  const drawBlankPage = useCallback(
    (widthPts: number, heightPts: number, widthPx: number) => {
      const canvas = canvasRef.current;
      if (!canvas || widthPx <= 0) return false;

      const scale = widthPx / widthPts;
      const w = Math.max(1, Math.floor(widthPts * scale));
      const h = Math.max(1, Math.floor(heightPts * scale));
      canvas.width = w;
      canvas.height = h;
      setCanvasPx({ width: w, height: h });
      setPagePts({ width: widthPts, height: heightPts });

      const ctx = canvas.getContext("2d");
      if (!ctx) return false;

      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, w, h);
      ctx.strokeStyle = "#d1d5db";
      ctx.lineWidth = 1;
      ctx.strokeRect(0.5, 0.5, w - 1, h - 1);
      ctx.fillStyle = "#6b7280";
      ctx.font = "14px sans-serif";
      ctx.fillText("New page preview", 16, 28);

      const size = Math.min(96, Math.max(48, w * 0.18));
      setQrSizePx(size);
      setQrPos(clampQr(w - size - 24, h - size - 24, size, w, h));
      return true;
    },
    [clampQr]
  );

  const drawPdfPage = useCallback(
    async (pdfData: ArrayBuffer, pageNum: number, widthPx: number) => {
      const canvas = canvasRef.current;
      if (!canvas || widthPx <= 0) return false;

      const pdfjs = await loadPdfJs();
      const doc = await pdfjs.getDocument({ data: pdfData.slice(0) }).promise;
      const page = await doc.getPage(pageNum);
      const baseViewport = page.getViewport({ scale: 1 });
      const scale = widthPx / baseViewport.width;
      const viewport = page.getViewport({ scale });

      const w = Math.max(1, Math.floor(viewport.width));
      const h = Math.max(1, Math.floor(viewport.height));
      canvas.width = w;
      canvas.height = h;
      setCanvasPx({ width: w, height: h });
      setPagePts({ width: baseViewport.width, height: baseViewport.height });

      const ctx = canvas.getContext("2d");
      if (!ctx) return false;

      ctx.clearRect(0, 0, w, h);
      await page.render({ canvasContext: ctx, viewport, canvas }).promise;

      const size = Math.min(96, Math.max(48, w * 0.18));
      setQrSizePx(size);
      setQrPos(clampQr(w - size - 24, h - size - 24, size, w, h));
      return true;
    },
    [clampQr]
  );

  useLayoutEffect(() => {
    if (stageWidth <= 0) return;

    let cancelled = false;

    (async () => {
      setLoading(true);
      setLoadError(null);

      try {
        if (targetMode === "new") {
          const ref = activePage || { width: 595, height: 842, pageNumber: 1 };
          const ok = drawBlankPage(ref.width, ref.height, stageWidth);
          if (!ok && !cancelled) throw new Error("Could not prepare page preview");
        } else {
          if (!pdfDataRef.current) {
            pdfDataRef.current = await fetchPdfBlob();
          }
          if (cancelled) return;
          const ok = await drawPdfPage(pdfDataRef.current, pageNumber, stageWidth);
          if (!ok && !cancelled) throw new Error("Could not render PDF page");
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : "Could not load PDF preview";
          setLoadError(message);
          setCanvasPx({ width: 0, height: 0 });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [stageWidth, targetMode, pageNumber, fileId, fetchPdfBlob, drawBlankPage, drawPdfPage, activePage]);

  const onPointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    setDragging(true);
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: qrPos.x, origY: qrPos.y };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setQrPos(
      clampQr(
        dragRef.current.origX + dx,
        dragRef.current.origY + dy,
        qrSizePx,
        canvasPx.width,
        canvasPx.height
      )
    );
  };

  const onPointerUp = () => setDragging(false);

  const handleSizeChange = (values: number[]) => {
    const next = values[0] ?? qrSizePx;
    setQrSizePx(next);
    setQrPos((p) => clampQr(p.x, p.y, next, canvasPx.width, canvasPx.height));
  };

  const handleApply = () => {
    if (canvasPx.width <= 0 || pagePts.width <= 0) return;
    const scale = canvasPx.width / pagePts.width;
    const qrWidthPts = qrSizePx / scale;
    const xPts = qrPos.x / scale;
    const yPts = pagePts.height - qrPos.y / scale - qrWidthPts;

    onApply({
      placement: "custom",
      pageNumber,
      x: Math.round(xPts * 100) / 100,
      y: Math.round(yPts * 100) / 100,
      qrWidth: Math.round(qrWidthPts * 100) / 100,
      addNewPage: targetMode === "new",
    });
  };

  const showQrOverlay = !loading && !loadError && !!qrDataUrl && canvasPx.width > 0;

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <Label className="text-xs">Target</Label>
          <RadioGroup
            value={targetMode}
            onValueChange={(v) => setTargetMode(v as "existing" | "new")}
            className="mt-1 space-y-2"
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem value="existing" id="qr-target-existing" />
              <Label htmlFor="qr-target-existing" className="cursor-pointer font-normal">
                Existing page
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="new" id="qr-target-new" />
              <Label htmlFor="qr-target-new" className="cursor-pointer font-normal">
                Add new page
              </Label>
            </div>
          </RadioGroup>
        </div>

        {targetMode === "existing" && (
          <div>
            <Label className="text-xs">Page</Label>
            <Select value={String(pageNumber)} onValueChange={(v) => setPageNumber(parseInt(v, 10))}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: pageCount }, (_, i) => i + 1).map((p) => (
                  <SelectItem key={p} value={String(p)}>
                    Page {p}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <Label className="text-xs">Drag the QR code to your preferred position</Label>
          <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
            <Move size={12} /> drag to move
          </span>
        </div>

        <div
          ref={stageRef}
          className="relative mx-auto min-h-[320px] w-full overflow-auto rounded-md border bg-muted/30 p-2"
        >
          <div className="relative mx-auto w-fit min-h-[280px]">
            <canvas
              ref={canvasRef}
              className={cn(
                "block max-w-full bg-white shadow-sm",
                loading && "opacity-40"
              )}
            />

            {showQrOverlay && (
              <div
                className={cn(
                  "absolute touch-none select-none border-2 border-primary bg-white shadow-lg",
                  dragging ? "cursor-grabbing" : "cursor-grab"
                )}
                style={{
                  left: qrPos.x,
                  top: qrPos.y,
                  width: qrSizePx,
                  height: qrSizePx,
                  zIndex: 10,
                }}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={qrDataUrl}
                  alt="QR code"
                  className="pointer-events-none h-full w-full object-contain"
                  draggable={false}
                />
              </div>
            )}

            {loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-background/60">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading page preview...
                </div>
              </div>
            )}

            {!loading && loadError && (
              <div className="absolute inset-0 flex items-center justify-center bg-background/80 p-4">
                <p className="text-center text-sm text-destructive">{loadError}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div>
        <Label className="text-xs">QR size</Label>
        <Slider
          className="mt-2"
          min={48}
          max={160}
          step={4}
          value={[qrSizePx]}
          onValueChange={handleSizeChange}
          disabled={!showQrOverlay}
        />
      </div>

      <p className="text-xs text-muted-foreground">
        Transaction {transactionNumber} — position is saved exactly where you place the QR on the page.
      </p>

      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="outline" onClick={onCancel} disabled={applying}>
          Cancel
        </Button>
        <Button onClick={handleApply} disabled={applying || loading || !!loadError || !showQrOverlay}>
          {applying && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Insert QR here
        </Button>
      </div>
    </div>
  );
}
